import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Download, Xmark } from 'iconoir-react';
import { dashboardDocument } from 'features/chat/utils/dashboard-document';
import { downloadDashboard } from 'features/chat/utils/dashboard-export';
import { useToast } from 'hooks/use-toast';
import { useIsMobile } from 'hooks/use-mobile';
import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { Sheet, SheetContent, SheetTitle } from 'components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { useAssistantFileViewerStore } from 'state/store/chat/use-assistant-file-viewer-store';
import type { Attachment } from 'features/chat/types/chat';
import { useAssistantFileContent } from 'features/chat/hooks/use-assistant-file-content';
import {
  useDashboardTitle,
  useDashboardVersion,
  useUntitledDashboardLabel,
  withDashboardVersion,
} from 'features/chat/hooks/use-dashboard-title';
import {
  getAttachmentDisplayName,
  isDashboardAttachment,
} from 'features/chat/utils/assistant-file-preview';
import {
  AgentWorkingCursor,
  useAgentWorkingLabel,
} from 'features/chat/components/AgentWorkingCursor';

// On desktop the panel is taken out of the chat row and pinned to the app column,
// so it fills the right side from the top edge down and the header buttons do not sit in a gap above it.
const dashboardPanelClass =
  'relative flex h-64 w-full shrink-0 flex-col overflow-hidden rounded-card bg-background mt-2 mb-3 ml-3 mr-2 md:absolute md:inset-y-0 md:right-0 md:z-10 md:h-auto md:w-[calc(66.666667%-0.75rem)] md:bg-background md:shadow-none';

// Below `md` the preview is a right-side sheet over the full-width chat instead of the
// in-flow card. Near-full width so charts stay readable at 320–375px (not `sm:max-w-sm`).
const dashboardSheetClass =
  'flex w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden border-border p-0 sm:max-w-none';

const emptyDashboardPanelClass = cn(
  dashboardPanelClass,
  'border-hairline border-grey-200 dark:border-border'
);

type AgentWorkingProps = {
  /** True while this conversation's agent run is in progress. */
  agentWorking?: boolean;
  agentName?: string | null;
};

export function DashboardCanvas({ agentWorking = false, agentName }: AgentWorkingProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const mutedColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-500';
  const title = useUntitledDashboardLabel();
  const workingLabel = useAgentWorkingLabel(agentName);
  const emptyMessage = t('DASHBOARD_EMPTY', { defaultValue: 'Nothing on this dashboard yet' });
  const moreLabel = t('MORE_OPTIONS', { defaultValue: 'More options' });
  const isMobile = useIsMobile();

  // Phone: one panel at a time. Creating a dashboard shows only the prompt and composer.
  if (isMobile) return null;

  return (
    <aside
      data-testid="dashboard-empty-panel"
      aria-label={title}
      className={emptyDashboardPanelClass}
    >
      <div className="flex h-11 shrink-0 items-center px-4">
        <h2 className={cn('min-w-0 truncate text-sm font-medium', titleColor)}>{title}</h2>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center px-6 text-center">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <p tabIndex={0} className={cn('text-lg font-medium', mutedColor)}>
                {emptyMessage}
              </p>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={6}
              className={cn(
                'rounded-nav border border-grey-100 bg-true-white px-3 py-1.5 text-sm font-normal leading-heading shadow-primary-soft dark:border-sidebar-border dark:bg-sidebar',
                titleColor
              )}
            >
              {moreLabel}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <AgentWorkingCursor active={agentWorking} label={workingLabel} />
    </aside>
  );
}

/**
 * Phone only: the empty canvas is not shown there, so the "agent is working" cursor runs over
 * the chat column instead. Render inside the `relative` chat column.
 */
export function DashboardChatWorkingCursor({
  agentWorking = false,
  agentName,
}: Readonly<AgentWorkingProps>) {
  const isMobile = useIsMobile();
  const workingLabel = useAgentWorkingLabel(agentName);

  if (!isMobile) return null;
  return <AgentWorkingCursor active={agentWorking} label={workingLabel} />;
}

export function DashboardChatPrompt() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';

  return (
    <div className="flex min-h-0 w-full flex-1 items-center justify-center px-6">
      <p className={cn('max-w-md text-center text-2xl font-medium leading-heading', titleColor)}>
        {t('DASHBOARD_PROMPT', { defaultValue: 'What should this dashboard show?' })}
      </p>
    </div>
  );
}

export function DashboardPreview({
  file,
  title: titleOverride,
  agentWorking = false,
  agentName,
}: AgentWorkingProps & {
  file: Attachment;
  title?: string;
}) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const setDashboardCanvasDismissed = useAssistantFileViewerStore(
    (state) => state.setDashboardCanvasDismissed
  );
  const {
    content,
    isLoading: isLoadingContent,
    error: contentError,
  } = useAssistantFileContent(file);
  const dashboardTitle = useDashboardTitle(file, content);
  const version = useDashboardVersion(file);
  const untitled = useUntitledDashboardLabel();
  const workingLabel = useAgentWorkingLabel(agentName);
  // Dashboards: their own title, then the conversation title; never the stored file name.
  const title = isDashboardAttachment(file.fileName, file.extension)
    ? withDashboardVersion(dashboardTitle || titleOverride?.trim() || untitled, version)
    : titleOverride?.trim() || getAttachmentDisplayName(file.fileName, file.extension);
  const previewDocument = useMemo(() => (content ? dashboardDocument(content) : ''), [content]);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const dismiss = () => setDashboardCanvasDismissed(true);
  // The sheet opens without a trigger, so focus goes back to whatever had it before it opened.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  // Phone: closing first lets the sheet slide out (Sheet's 300ms exit); the canvas is
  // dismissed once Radix has unmounted it. A resize past `md` also unmounts the sheet but
  // is not a dismissal, hence the ref.
  const [sheetOpen, setSheetOpen] = useState(true);
  const sheetClosingRef = useRef(false);
  const closeSheet = () => {
    sheetClosingRef.current = true;
    setSheetOpen(false);
  };
  // A new file arriving while the sheet slides out keeps it open for that file.
  useEffect(() => {
    sheetClosingRef.current = false;
    setSheetOpen(true);
  }, [file]);
  // Radix reports the close a tick after the sheet unmounts. If the whole preview unmounts
  // mid-exit (the user left the conversation), that late report must not dismiss the next
  // page's dashboard.
  useEffect(
    () => () => {
      sheetClosingRef.current = false;
    },
    []
  );

  // The stored file is the raw `.dashboard` source; users get the same self-contained
  // zip (index.html, pinned assets, data) as from the gallery and the file viewer.
  const [isExporting, setIsExporting] = useState(false);
  const exportDashboard = async () => {
    if (!content || isExporting) return;
    setIsExporting(true);
    try {
      await downloadDashboard(content);
    } catch {
      toast({
        variant: 'destructive',
        title: t('DASHBOARD_EXPORT_FAILED', { defaultValue: 'Export failed' }),
        description: t('DASHBOARD_EXPORT_FAILED_DESCRIPTION', {
          defaultValue: 'Unable to export this dashboard. Please try again.',
        }),
      });
    } finally {
      setIsExporting(false);
    }
  };
  let statusMessage = t('DASHBOARD_NO_PREVIEW', { defaultValue: 'No preview available.' });
  if (isLoadingContent) {
    statusMessage = t('DASHBOARD_LOADING', { defaultValue: 'Loading dashboard…' });
  }
  if (contentError) {
    statusMessage = t('DASHBOARD_LOAD_FAILED', { defaultValue: 'Could not load this dashboard.' });
  }

  const titleClass = cn('min-w-0 truncate text-sm font-medium', titleColor);
  const header = (titleNode: ReactNode) => (
    <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border px-3">
      {titleNode}
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          size="sm"
          // Phone tap targets are at least 40px; the desktop header keeps its compact button.
          className={cn('gap-1.5 rounded-nav px-3 font-medium', isMobile ? 'h-10' : 'h-7')}
          disabled={!content || isExporting}
          onClick={exportDashboard}
        >
          <Download strokeWidth={1.75} aria-hidden />
          {t('EXPORT', { defaultValue: 'Export' })}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('h-10 w-10', titleColor)}
          aria-label={t('CLOSE', { defaultValue: 'Close' })}
          onClick={isMobile ? closeSheet : dismiss}
        >
          <Xmark strokeWidth={1.75} aria-hidden />
        </Button>
      </div>
    </div>
  );
  const body = content ? (
    // Untrusted generated HTML: scripts may run for charts, but only in an opaque,
    // network-denied sandbox (no allow-same-origin; CSP injected by dashboardDocument).
    <iframe
      title={title}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      srcDoc={previewDocument}
      className="min-h-0 w-full flex-1 border-0 bg-background"
    />
  ) : (
    <div className="min-h-0 flex-1 p-4" role="status">
      {statusMessage}
    </div>
  );

  if (isMobile) {
    return (
      // Overlay click and Escape close exactly like Close; a later HTML file opens it again.
      <Sheet open={sheetOpen} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent
          side="right"
          hideClose
          aria-describedby={undefined}
          data-testid="dashboard-html-preview"
          className={dashboardSheetClass}
          onOpenAutoFocus={() => {
            returnFocusRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
            if (sheetClosingRef.current) dismiss();
          }}
        >
          {header(<SheetTitle className={titleClass}>{title}</SheetTitle>)}
          {body}
          <AgentWorkingCursor active={agentWorking} label={workingLabel} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      data-testid="dashboard-html-preview"
      aria-label={title}
      className={cn(dashboardPanelClass, 'border-hairline border-border')}
    >
      {header(<h2 className={titleClass}>{title}</h2>)}
      {body}
      <AgentWorkingCursor active={agentWorking} label={workingLabel} />
    </aside>
  );
}
